import PropTypes from 'prop-types';
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha, keyframes } from '@mui/material/styles';
import { toast } from 'react-toastify';
import {
  Box,
  List,
  Badge,
  Button,
  Chip,
  Divider,
  Popover,
  Tooltip,
  Typography,
  IconButton,
  Stack,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
} from '@mui/material';
import {
  viewAllNotification,
  notificationList,
  clearAllNotifications,
  markNotificationRead,
} from '../../../api/notifications';
import { useNotifications } from '../../../contexts/NotificationContext';
import { fToNow } from '../../../utils/formatTime';
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';

// ─── Category config ──────────────────────────────────────────────────────────

const CATEGORY_CONFIG = {
  user_signup:      { label: 'Signup',    icon: 'eva:person-add-fill',       color: 'info'    },
  trial_activated:  { label: 'Trial',     icon: 'eva:flash-fill',            color: 'warning' },
  sub_purchased:    { label: 'Purchase',  icon: 'eva:credit-card-fill',      color: 'success' },
  sub_renewed:      { label: 'Renewal',   icon: 'eva:refresh-fill',          color: 'success' },
  sub_cancelled:    { label: 'Cancelled', icon: 'eva:close-circle-fill',     color: 'error'   },
  payment_refunded: { label: 'Refund',    icon: 'eva:corner-up-left-fill',   color: 'error'   },
  payment_failed:   { label: 'Failed',    icon: 'eva:alert-triangle-fill',   color: 'error'   },
  plan_expiring:    { label: 'Expiring',  icon: 'eva:clock-fill',            color: 'warning' },
  plan_expired:     { label: 'Expired',   icon: 'eva:alert-circle-fill',     color: 'error'   },
  data_added:       { label: 'Data',      icon: 'eva:bar-chart-2-fill',      color: 'primary' },
  system_alert:     { label: 'System',    icon: 'eva:settings-2-fill',       color: 'default' },
};

const PRIORITY_BORDER = { 4: 'error.main', 3: 'warning.main', 2: 'primary.light', 1: 'divider' };

const COLOR_MAP = {
  info:    { bg: (t) => alpha(t.palette.info.main,    0.12), fg: 'info.main'    },
  warning: { bg: (t) => alpha(t.palette.warning.main, 0.12), fg: 'warning.main' },
  success: { bg: (t) => alpha(t.palette.success.main, 0.12), fg: 'success.main' },
  error:   { bg: (t) => alpha(t.palette.error.main,   0.12), fg: 'error.main'   },
  primary: { bg: (t) => alpha(t.palette.primary.main, 0.12), fg: 'primary.main' },
  default: { bg: 'grey.200',                                  fg: 'text.secondary' },
};

const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.8; }
  70%  { transform: scale(1.5); opacity: 0;   }
  100% { transform: scale(1.5); opacity: 0;   }
`;

function getCategoryConfig(type) {
  return CATEGORY_CONFIG[type] || CATEGORY_CONFIG.system_alert;
}

function buildMetaLine(metadata) {
  if (!metadata) return null;
  const parts = [];
  if (metadata.userName)          parts.push(metadata.userName);
  if (metadata.planName)          parts.push(metadata.planName);
  if (metadata.amount != null)    parts.push(`₹${Number(metadata.amount).toLocaleString('en-IN')}`);
  if (metadata.phone && !metadata.userName) parts.push(metadata.phone);
  return parts.length ? parts.join(' · ') : null;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function NotificationsPopover() {
  const navigate = useNavigate();

  // Counts + latest items come from the shared context (SSE lives there)
  const { unread, critical, latestItems, syncCounts, markCountsRead, decrementUnread, prependItem } =
    useNotifications();

  // Local list state — seeded from context when the popover opens
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(null);

  // ── Open: fetch full sorted list, seed local state and sync context counts ──
  const handleOpen = useCallback(async (e) => {
    setOpen(e.currentTarget);
    try {
      const res     = await notificationList({ page: 1, limit: 20 });
      const payload = res?.data ?? res;
      const items   = payload.notifications ?? payload.notification ?? [];
      setNotifications(items);
      syncCounts(payload);
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.clear(); window.location.reload(); }
      toast.error(err?.response?.data?.error || 'Failed to load notifications');
    }
  }, [syncCounts]);

  const handleClose = () => setOpen(null);

  // Keep local list fresh when SSE pushes a new item while popover is open
  const mergeNewItem = useCallback((item) => {
    setNotifications((prev) => {
      if (prev.some((n) => n.id === item.id)) return prev;
      return [item, ...prev].slice(0, 20);
    });
  }, []);

  // Sync any SSE-pushed items that arrived while popover was closed
  // (latestItems is the context buffer — use it to seed on open)
  const handleOpenWithContext = useCallback((e) => {
    // Seed immediately from context so there's no flash of empty state
    setNotifications(latestItems);
    handleOpen(e);
  }, [latestItems, handleOpen]);

  // ── Mark all read ──────────────────────────────────────────────────────────
  const handleMarkAllAsRead = async () => {
    try {
      await viewAllNotification();
      setNotifications((prev) => prev.map((n) => ({ ...n, isViewed: true })));
      markCountsRead();
    } catch (_) {
      toast.error('Failed to mark notifications as read');
    }
  };

  // ── Per-item click: mark read + navigate ──────────────────────────────────
  const handleItemClick = async (notification) => {
    if (!notification.isViewed) {
      try {
        await markNotificationRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, isViewed: true } : n))
        );
        decrementUnread(notification.id, notification.priority >= 4);
      } catch (_) { /* non-critical */ }
    }
    if (notification.path) {
      handleClose();
      navigate(`/admin${notification.path.startsWith('/') ? '' : '/'}${notification.path}`);
    }
  };

  // ── Clear read ─────────────────────────────────────────────────────────────
  const handleClearRead = async () => {
    try {
      await clearAllNotifications();
      setNotifications((prev) => prev.filter((n) => !n.isViewed));
      toast.success('Read notifications cleared');
    } catch (_) {
      toast.error('Failed to clear notifications');
    }
  };

  const readCount = notifications.filter((n) => n.isViewed).length;

  return (
    <>
      {/* ── Bell button ── */}
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        {critical > 0 && (
          <Box
            sx={{
              position: 'absolute', inset: 0,
              borderRadius: '50%',
              border: '2px solid', borderColor: 'error.main',
              animation: `${pulseRing} 1.4s ease-out infinite`,
              pointerEvents: 'none',
            }}
          />
        )}
        <IconButton color={open ? 'primary' : 'default'} onClick={handleOpenWithContext} sx={{ width: 40, height: 40 }}>
          <Badge badgeContent={unread} color="error" max={99}>
            <Iconify icon="eva:bell-fill" />
          </Badge>
        </IconButton>
      </Box>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { mt: 1.5, width: 420, borderRadius: 2, overflow: 'hidden', boxShadow: (t) => t.shadows[20] },
        }}
      >
        {/* ── Header ── */}
        <Stack
          direction="row" alignItems="center" justifyContent="space-between"
          sx={{ py: 2, px: 2.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: (t) => alpha(t.palette.grey[500], 0.04) }}
        >
          <Stack spacing={0.25}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notifications</Typography>
              {unread > 0 && (
                <Box component="span" sx={{ px: 0.875, py: 0.2, borderRadius: 0.75, fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.6, bgcolor: (t) => alpha(t.palette.error.main, 0.12), color: 'error.dark' }}>
                  {unread} new
                </Box>
              )}
              {critical > 0 && (
                <Box component="span" sx={{ px: 0.875, py: 0.2, borderRadius: 0.75, fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.6, bgcolor: (t) => alpha(t.palette.error.main, 0.2), color: 'error.dark', border: '1px solid', borderColor: (t) => alpha(t.palette.error.main, 0.4) }}>
                  {critical} critical
                </Box>
              )}
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {unread > 0 ? `${unread} unread notification${unread > 1 ? 's' : ''}` : 'All caught up'}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.5}>
            {unread > 0 && (
              <Tooltip title="Mark all as read">
                <IconButton size="small" color="primary" onClick={handleMarkAllAsRead}>
                  <Iconify icon="eva:done-all-fill" width={18} height={18} />
                </IconButton>
              </Tooltip>
            )}
            {readCount > 0 && (
              <Tooltip title={`Clear ${readCount} read`}>
                <IconButton size="small" onClick={handleClearRead} sx={{ color: 'text.secondary' }}>
                  <Iconify icon="eva:trash-2-outline" width={18} height={18} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {/* ── Body ── */}
        <Scrollbar sx={{ maxHeight: 440 }}>
          {notifications.length === 0 ? (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 7, px: 2 }}>
              <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Iconify icon="eva:bell-outline" width={30} height={30} sx={{ color: 'text.disabled' }} />
              </Box>
              <Stack spacing={0.5} alignItems="center">
                <Typography variant="subtitle2">No notifications</Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', textAlign: 'center', maxWidth: 220 }}>
                  System events and activity alerts will appear here.
                </Typography>
              </Stack>
            </Stack>
          ) : (
            <List disablePadding>
              {notifications.map((n, idx) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  isLast={idx === notifications.length - 1}
                  onClick={() => handleItemClick(n)}
                />
              ))}
            </List>
          )}
        </Scrollbar>

        {/* ── Footer ── */}
        {notifications.length > 0 && (
          <>
            <Divider />
            <Stack direction="row" spacing={1} sx={{ p: 1 }}>
              {unread > 0 && (
                <Button size="small" startIcon={<Iconify icon="eva:done-all-fill" width={15} />} onClick={handleMarkAllAsRead} sx={{ flex: 1, borderRadius: 1.5, fontSize: '0.78rem' }}>
                  Mark all read
                </Button>
              )}
              <Button
                size="small" variant="outlined"
                startIcon={<Iconify icon="eva:external-link-fill" width={15} />}
                onClick={() => { handleClose(); navigate('/admin/dashboard/notification-center'); }}
                sx={{ flex: 1, borderRadius: 1.5, fontSize: '0.78rem' }}
              >
                View all
              </Button>
            </Stack>
          </>
        )}
      </Popover>
    </>
  );
}

// ─── NotificationItem ─────────────────────────────────────────────────────────

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.number, title: PropTypes.string, path: PropTypes.string,
    isViewed: PropTypes.bool, createdAt: PropTypes.string,
    type: PropTypes.string, priority: PropTypes.number, metadata: PropTypes.object,
  }),
  isLast:  PropTypes.bool,
  onClick: PropTypes.func,
};

function NotificationItem({ notification, isLast, onClick }) {
  const isUnread  = !notification.isViewed;
  const cfg       = getCategoryConfig(notification.type);
  const colors    = COLOR_MAP[cfg.color] || COLOR_MAP.default;
  const metaLine  = buildMetaLine(notification.metadata);
  const borderClr = PRIORITY_BORDER[notification.priority] || PRIORITY_BORDER[2];

  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        py: 1.5, px: 2.5,
        borderBottom: isLast ? 'none' : '1px solid', borderColor: 'divider',
        borderLeft: '3px solid', borderLeftColor: borderClr,
        bgcolor: isUnread ? (t) => alpha(t.palette.primary.main, 0.04) : 'transparent',
        '&:hover': { bgcolor: (t) => isUnread ? alpha(t.palette.primary.main, 0.08) : alpha(t.palette.grey[500], 0.08) },
        alignItems: 'flex-start', transition: 'background-color 150ms ease',
      }}
    >
      <ListItemAvatar sx={{ mt: 0.25, minWidth: 44 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 1.5, bgcolor: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Iconify icon={cfg.icon} width={18} height={18} sx={{ color: colors.fg }} />
        </Box>
      </ListItemAvatar>

      <ListItemText
        disableTypography
        primary={
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <Chip label={cfg.label} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: colors.bg, color: colors.fg, '& .MuiChip-label': { px: 0.75 } }} />
            {notification.priority >= 4 && (
              <Chip label="Critical" size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: (t) => alpha(t.palette.error.main, 0.15), color: 'error.dark', '& .MuiChip-label': { px: 0.75 } }} />
            )}
          </Stack>
        }
        secondary={
          <Box sx={{ mt: 0.4 }}>
            <Typography variant="body2" sx={{ fontWeight: isUnread ? 600 : 400, color: isUnread ? 'text.primary' : 'text.secondary', lineHeight: 1.4, fontSize: '0.82rem' }}>
              {notification.title}
            </Typography>
            {metaLine && (
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.25 }}>
                {metaLine}
              </Typography>
            )}
            <Typography variant="caption" sx={{ mt: 0.4, display: 'flex', alignItems: 'center', color: 'text.disabled' }}>
              <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 11, height: 11 }} />
              {fToNow(notification.createdAt)}
            </Typography>
          </Box>
        }
        sx={{ my: 0 }}
      />

      {isUnread && (
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0, mt: 1.5, ml: 1 }} />
      )}
    </ListItemButton>
  );
}
